import { z } from "zod"
import { optionalEmailSchema } from "../../common/email-validator"
import { SaveCustomerContactDetailsSchema } from "../../customer"
import { BookingStepType } from "../booking-step-order"
import { CustomBookingSection } from "../custom-booking-section"
import {
  DamperBrand,
  DamperServiceActionName,
  DamperServicePrice,
} from "../damper-service"
import { CreateDamperBookingInfoSchema } from "../damper-service"
import { Discount } from "../discount"
import { ServiceBikeType } from "../index"
import { JobSizeSchema } from "../job"
import {
  BookingDayStatuses,
  ServiceAvailability,
} from "../service-availability"
import { PricingMode } from "../service-fee"
import { ServicePackage } from "../service-package"
import { ServiceWash } from "../service-wash"
import {
  CommonBookingSchema,
  CommonDataRequiredForBooking,
} from "./service-booking"

export interface StandardFeeInfo {
  id: string
  name: string
  price: number
  isActive: boolean
  pricingMode: PricingMode
  percentageRate: number | null
  minPrice: number | null
  maxPrice: number | null
}

export interface DataRequiredForMaintenanceBooking
  extends CommonDataRequiredForBooking {
  bookableServicePackages: ServicePackage[]
  bookableWashes: ServiceWash[]
  bookableCustomSections: CustomBookingSection[]
  // Types the customer can pick: the store's supported types, or bicycles
  // when the store hasn't set any up
  bookableBikeTypes: ServiceBikeType[]
  availability: ServiceAvailability
  // Availability for cargo bikes when the store limits them per day, otherwise null
  cargoBikeAvailability: ServiceAvailability | null
  // Per-day status (available, last slot, full, unavailable) for the calendar
  dayStatuses: BookingDayStatuses
  // Per-day status for cargo bikes when the store limits them per day, otherwise null
  cargoBikeDayStatuses: BookingDayStatuses | null
  // Damper service data (only included if store offers damper service)
  damperBrands?: DamperBrand[]
  damperServicePrices?: DamperServicePrice[]
  damperServiceActionNames?: DamperServiceActionName[]
  hasDamperService?: boolean
  // Discount codes
  hasActiveDiscountCodes?: boolean
  // Booking step configuration (order, visibility, custom names)
  bookingSteps?: Array<{
    stepType: BookingStepType
    customSectionId: string | null
    customName: string | null
    displayOrder: number
    enabled: boolean
  }>
  // Standard system fees
  standardFees: {
    environmental?: StandardFeeInfo
    consumables?: StandardFeeInfo
    ebikeAddon?: StandardFeeInfo
    cargoBikeAddon?: StandardFeeInfo
  }
}

export const CustomerDetailsForMaintenanceBookingSchema =
  SaveCustomerContactDetailsSchema.extend({
    email: optionalEmailSchema().optional(),
  })

export const CreateMaintenanceBookingSchema = CommonBookingSchema.extend({
  customer: CustomerDetailsForMaintenanceBookingSchema,
  selectedWashId: z.string().uuid().nullable(),
  selectedServicePackageIds: z.array(z.string().uuid()),
  selectedCustomItemIds: z.array(z.string().uuid()).optional(),
  bike: z
    .object({
      type: z.nativeEnum(ServiceBikeType),
      isElectric: z.boolean().nullable(),
    })
    .optional(),
  discountCodeId: z.string().uuid().optional(),
  damperInfos: z.array(CreateDamperBookingInfoSchema).optional(),
  // Optional contact-person linkage. Picking a contact person determines both
  // the company and the avdelning at read time.
  companyContactPersonId: z.string().uuid().nullable().optional(),
  reference: z.string().max(500).nullable().optional(),
  eServiceNumber: z.string().trim().max(100).nullable().optional(),
  // Only mechanics set this; customer bookings always get the default size.
  size: JobSizeSchema.optional(),
})

export type CreateMaintenanceBooking = z.infer<
  typeof CreateMaintenanceBookingSchema
>

export const MaintenanceBikeEntrySchema = z.object({
  bike: z
    .object({
      type: z.nativeEnum(ServiceBikeType),
      isElectric: z.boolean().nullable(),
    })
    .optional(),
  selectedWashId: z.string().uuid().nullable(),
  selectedServicePackageIds: z.array(z.string().uuid()),
  selectedCustomItemIds: z.array(z.string().uuid()).optional(),
  description: z.string().nullable(),
  size: JobSizeSchema.optional(),
})

export type MaintenanceBikeEntry = z.infer<typeof MaintenanceBikeEntrySchema>

export const CreateMaintenanceBookingMultipleBikesSchema =
  CommonBookingSchema.omit({ description: true }).extend({
    customer: CustomerDetailsForMaintenanceBookingSchema,
    discountCodeId: z.string().uuid().optional(),
    bikes: z.array(MaintenanceBikeEntrySchema).min(1).max(10),
    // Optional contact-person linkage (applied to all created jobs).
    companyContactPersonId: z.string().uuid().nullable().optional(),
    reference: z.string().max(500).nullable().optional(),
    eServiceNumber: z.string().trim().max(100).nullable().optional(),
  })

export type CreateMaintenanceBookingMultipleBikes = z.infer<
  typeof CreateMaintenanceBookingMultipleBikesSchema
>

export const CreateMaintenanceBookingUnionSchema = z
  .record(z.string(), z.unknown())
  .transform(
    (
      val,
      ctx,
    ): CreateMaintenanceBooking | CreateMaintenanceBookingMultipleBikes => {
      if ("bikes" in val && Array.isArray(val.bikes)) {
        const result =
          CreateMaintenanceBookingMultipleBikesSchema.safeParse(val)
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue(issue)
          }
          return z.NEVER
        }
        return result.data
      }
      const result = CreateMaintenanceBookingSchema.safeParse(val)
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue(issue)
        }
        return z.NEVER
      }
      return result.data
    },
  )

export interface PackageInBooking {
  originalPackageId: string
  snapshotPackageId: string
  name: string
  price: number
  priceDiscount: Discount | null
  priceAfterDiscount: number | null
  wash?: {
    id: string
    name: string
    price: number
  } | null
}
